// Primitive game actions (412).
//
// These are the verbs the rules define -- Draw, Kill, Deal, Channel, Recycle and
// the rest. Both the turn machine and the effect interpreter call them, so the
// rule for each one lives in exactly one place: if Burn Out is wrong, it is
// wrong once.
//
// Nothing here decides *whether* something may happen. Legality is the engine's
// job; these functions assume they have already been cleared to run.

import { isUnitType } from './cards.ts';
import type { Rules } from './state.ts';
import {
  OPPONENT, baseLocation, battlefield, boardObjects, emptyPool, moveToZone, removeFromZone, victoryTarget, zoneList,
} from './state.ts';
import type {
  SpendOnly,
  Domain, GameObject, GameState, Keyword, Location, ObjectId, PlayerId,
} from './types.ts';
import { DOMAINS, sameLocation } from './types.ts';
import { shuffle } from './rng.ts';

export const obj = (state: GameState, id: ObjectId): GameObject => {
  const o = state.objects[id];
  if (!o) throw new Error(`no object ${id}`);
  return o;
};

const touch = (state: GameState) => { state.version++; };

// -- cards in and out of zones ----------------------------------------------

/** 413. Draws one card, burning out first if the deck is empty (413.4). */

/**
 * Records something for the trigger system to pick up.
 *
 * Deliberately not a direct call into the trigger machinery: ops sits below the
 * engine, and reaching upward would be a cycle. The engine drains this queue.
 */
function announce(
  state: GameState, on: string, player: PlayerId, subject?: ObjectId,
): void {
  state.pendingTriggers.push(subject === undefined
    ? { on, player }
    : { on, player, subject });
}

export function draw(state: GameState, rules: Rules, player: PlayerId, count = 1): void {
  if (count > 0) announce(state, 'youDraw', player);
  for (let i = 0; i < count; i++) {
    if (zoneList(state, 'mainDeck', player).length === 0) {
      burnOut(state, rules, player);
      // 431.3: if the deck is still empty the player cannot draw at all.
      if (zoneList(state, 'mainDeck', player).length === 0) continue;
    }
    const deck = zoneList(state, 'mainDeck', player);
    const id = deck[deck.length - 1];
    if (id === undefined) continue;
    const card = obj(state, id);
    moveToZone(state, rules, card, { kind: 'hand', player });
    state.events.push({ t: 'draw', player, object: id, card: card.card });
  }
}

/**
 * 431. Recycle the trash into the main deck, give an opponent a point, then
 * carry on with whatever action caused it.
 */
export function burnOut(state: GameState, rules: Rules, player: PlayerId): void {
  const trash = [...zoneList(state, 'trash', player)];
  for (const id of trash) {
    const card = obj(state, id);
    moveToZone(state, rules, card, { kind: 'mainDeck', player }, { toBottom: true });
  }
  // 431.2.b: cards recycled together are randomised.
  shuffle(state, zoneList(state, 'mainDeck', player));

  const rival = OPPONENT(player);
  gainPoints(state, rules, rival, 1);
  state.events.push({ t: 'burnOut', player, gavePointTo: rival });
}

export function discard(
  state: GameState, rules: Rules, player: PlayerId, ids: ObjectId[],
): void {
  if (ids.length > 0) announce(state, 'youDiscard', player);
  for (const id of ids) {
    const card = obj(state, id);
    moveToZone(state, rules, card, { kind: 'trash', player });
    state.events.push({ t: 'discard', player, object: id, card: card.card });
    // The card's own trigger, which is a different event from its controller
    // having discarded something.
    announce(state, 'discarded', player, id);
  }
}

/**
 * 416. Cards go to the bottom of the deck they belong to: runes to the Rune
 * Deck, everything else to the Main Deck, always their owner's (416.1.c).
 */
export function recycle(state: GameState, rules: Rules, ids: ObjectId[]): void {
  const first = ids.length > 0 ? state.objects[ids[0]!] : undefined;
  if (first) announce(state, 'youRecycle', first.controller);
  const moved: ObjectId[] = [];
  const toDeck: { id: ObjectId; card: GameObject; deck: 'mainDeck' | 'runeDeck' }[] = [];
  for (const id of ids) {
    const card = obj(state, id);
    const def = rules.index.get(card.card);
    // 185.3: a token put into a non-board zone simply ceases to exist.
    if (def.type === 'token') {
      removeFromZone(state, card);
      delete state.objects[id];
      continue;
    }
    const deck = def.type === 'rune' ? 'runeDeck' : 'mainDeck';
    toDeck.push({ id, card, deck });
  }

  // 416.5. "If 2 or more cards are Recycled to the Main Deck simultaneously,
  // they are placed on the bottom of that deck in a random order."
  //
  // Not a nicety: without it the bottom of the deck is in whatever order the
  // player named the cards, so a player mulliganing two cards decides which of
  // them they will see again first. 416.5.a leaves the *Rune* Deck in the
  // owner's chosen order, which is why only one of the two is shuffled.
  const main = toDeck.filter((c) => c.deck === 'mainDeck');
  if (main.length > 1) {
    const order = shuffle(state, main.map((c) => c.id));
    main.sort((x, y) => order.indexOf(x.id) - order.indexOf(y.id));
  }
  for (const { id, card, deck } of [...main, ...toDeck.filter((c) => c.deck !== 'mainDeck')]) {
    moveToZone(state, rules, card, { kind: deck, player: card.owner }, { toBottom: true });
    moved.push(id);
  }
  if (moved.length) {
    state.events.push({ t: 'recycle', player: obj(state, moved[0]!).owner, objects: moved });
  }
}

/** 430. Puts runes from the top of the Rune Deck onto the board. */
export function channel(
  state: GameState, rules: Rules, player: PlayerId, count: number, exhausted = false,
): void {
  for (let i = 0; i < count; i++) {
    const deck = zoneList(state, 'runeDeck', player);
    const id = deck[deck.length - 1];
    if (id === undefined) return; // 430.3: channel as many as possible
    const rune = obj(state, id);
    moveToZone(state, rules, rune, { kind: 'board' });
    rune.location = baseLocation(player);
    rune.controller = player;
    rune.exhausted = exhausted;
    state.events.push({ t: 'channel', player, object: id, card: rune.card, exhausted });
  }
}

// -- board state -------------------------------------------------------------

export function exhaust(state: GameState, target: GameObject): void {
  if (target.exhausted) return;
  target.exhausted = true;
  state.events.push({ t: 'exhaust', object: target.id });
  touch(state);
}

export function ready(state: GameState, target: GameObject): void {
  if (!target.exhausted) return;
  target.exhausted = false;
  announce(state, 'readied', target.controller, target.id);
  state.events.push({ t: 'ready', object: target.id });
  touch(state);
}

/** 417. Marks damage. Only positive amounts are valid damage (417.1.e). */
export function deal(
  state: GameState, target: GameObject, amount: number, source: ObjectId | null,
): void {
  if (amount <= 0) return;
  target.damage += amount;
  // 705. Remembered separately from the marks: healing wipes those, and "if I
  // haven't been dealt damage this turn" asks whether it happened at all.
  target.damagedThisTurn = true;
  state.events.push({ t: 'damage', object: target.id, amount, source });
  touch(state);
}

/** 418. Clears marked damage. */
export function heal(state: GameState, target: GameObject): void {
  if (target.damage === 0) return;
  target.damage = 0;
  state.events.push({ t: 'heal', object: target.id });
  touch(state);
}

/** 423. Binary; a stunned unit adds no Might in the combat damage step. */
export function stun(state: GameState, target: GameObject): void {
  if (target.statuses.includes('stunned')) return;
  target.statuses.push('stunned');
  announce(state, 'youStun', OPPONENT(target.controller), target.id);
  touch(state);
}

/**
 * 441. Empower is a binary status, like Buff -- except on the cards that print
 * a higher cap, where the status stays binary and the count goes up behind it.
 */
export function empower(
  state: GameState, target: GameObject,
  /** 441. "I can be [Empowered] up to three times." One unless the card says. */
  limit = 1,
): boolean {
  if ((target.empowerCount ?? (target.statuses.includes('empowered') ? 1 : 0)) >= limit) {
    return false;
  }
  target.empowerCount = (target.empowerCount ?? 0) + 1;
  if (!target.statuses.includes('empowered')) target.statuses.push('empowered');
  announce(state, 'youEmpower', target.controller, target.id);
  announce(state, 'becomesEmpowered', target.controller, target.id);
  touch(state);
  return true;
}

export function disempower(state: GameState, target: GameObject): void {
  if (!target.statuses.includes('empowered')) return;
  target.statuses = target.statuses.filter((s) => s !== 'empowered');
  // Every stack comes off at once: nothing prints partial disempowerment.
  target.empowerCount = 0;
  touch(state);
}

/**
 * 440. Burn: move cards from the top of the Main Deck straight to the trash.
 * Running out triggers a Burn Out like any other forced deck movement (431.1.b).
 */
export function burn(
  state: GameState, rules: Rules, player: PlayerId, count: number,
  /** 440. What went to the trash, for "when you burn a unit this way". */
  burned?: ObjectId[],
): void {
  for (let i = 0; i < count; i++) {
    const deck = zoneList(state, 'mainDeck', player);
    if (deck.length === 0) {
      burnOut(state, rules, player);
      if (zoneList(state, 'mainDeck', player).length === 0) return;
    }
    const list = zoneList(state, 'mainDeck', player);
    const id = list[list.length - 1];
    if (id === undefined) return;
    burned?.push(id);
    moveToZone(state, rules, obj(state, id), { kind: 'trash', player });
  }
}

/** 424. Reveals the top N of a deck. Cards stay in the deck while revealed. */
export function revealTop(
  state: GameState, player: PlayerId, count: number,
): ObjectId[] {
  const deck = zoneList(state, 'mainDeck', player);
  return deck.slice(Math.max(0, deck.length - count)).reverse();
}

/** Returns a permanent on the board to its owner's hand. */
export function returnToHand(state: GameState, rules: Rules, target: GameObject): void {
  moveToZone(state, rules, target, { kind: 'hand', player: target.owner });
}

/** 426. At most one buff counter per unit. */
export function buff(
  state: GameState, target: GameObject,
  /** 137.2. One buff at a time unless a card lifts the cap. */
  limit = 1,
): boolean {
  const held = target.buffCount ?? (target.statuses.includes('buffed') ? 1 : 0);
  if (held >= limit) return false;
  target.buffCount = held + 1;
  if (!target.statuses.includes('buffed')) target.statuses.push('buffed');
  announce(state, 'youBuff', target.controller, target.id);
  announce(state, 'becomesBuffed', target.controller, target.id);
  touch(state);
  return true;
}


/**
 * 137.2. Removes one buff. The `buffed` status only clears when the last one
 * goes -- a unit whose cap was lifted can be carrying several.
 */
export function spendBuff(target: GameObject): void {
  const held = target.buffCount ?? (target.statuses.includes('buffed') ? 1 : 0);
  if (held <= 0) return;
  target.buffCount = held - 1;
  if (target.buffCount === 0) {
    target.statuses = target.statuses.filter((s) => s !== 'buffed');
  }
}

/** 428. A permanent goes from the board straight to its owner's trash. */
export function kill(
  state: GameState, rules: Rules, target: GameObject, by: ObjectId | null,
): void {
  if (target.zone.kind !== 'board') return;
  const card = target.card;
  const def = rules.index.get(card);

  announce(state, 'unitDies', target.controller, target.id);
  if (isUnitType(def.type)) {
    state.players[target.controller].unitsDiedThisTurn++;
    // 315. Temporary kills land in the Beginning Phase, before scoring, and a
    // card can ask about exactly that window.
    if (state.phase === 'beginning') {
      state.players[target.controller].unitsDiedInBeginningPhase++;
    }
  }
  const killer = by !== null ? state.objects[by] : undefined;
  if (killer) announce(state, 'youKillUnit', killer.controller, target.id);

  if (def.type === 'token') {
    // 186.1: a token ceases to exist rather than going to the trash.
    for (const id of [...target.attachments]) {
      const att = state.objects[id];
      if (att) recallToBase(state, att);
    }
    removeFromZone(state, target);
    delete state.objects[target.id];
  } else {
    moveToZone(state, rules, target, { kind: 'trash', player: target.owner });
  }
  state.events.push({ t: 'kill', object: target.id, card, by });
  touch(state);
}

/** 427. Banish is not a subset of Kill (427.2.a). */
export function banish(
  state: GameState, rules: Rules, target: GameObject,
  // 447. The card doing the banishing, for "banished with me" to read back.
  by: ObjectId | null = null,
): void {
  announce(state, 'youBanish', target.controller, target.id);
  target.banishedBy = by;
  moveToZone(state, rules, target, { kind: 'banishment', player: target.owner });
}

/** 445. A permanent changing location on the board. */
/**
 * 719.3.a. Drags a top-most card's attachments along to its new location.
 *
 * "A Top-Most Card and all cards Attached to it are at the same location. When
 * the Top-Most Card changes locations, all Attached cards change locations
 * with it." This is deliberately not a Move (446.1) and raises no move
 * triggers: the attachment did not move, it was carried, and 718.5.c says it
 * cannot be moved separately at all.
 *
 * Without it an equipped unit walked to a battlefield and left its Equipment
 * standing in the base -- still attached, still granting its abilities, but at
 * the wrong place. That is invisible until something asks *where*: an
 * Equipment's own "when I attack or defend, deal 2 to an enemy unit here"
 * looks around the base and finds nobody.
 */
function carryAttachments(state: GameState, host: GameObject, to: Location): void {
  for (const id of host.attachments) {
    const att = state.objects[id];
    if (!att || sameLocation(att.location, to)) continue;
    att.location = to;
    // An attachment may itself be a top-most card for something else.
    carryAttachments(state, att, to);
  }
}

export function move(
  state: GameState, target: GameObject, to: Location,
  // 144. Who moved it. Not the same as who controls it -- an effect can move
  // an enemy unit -- and "when you move an enemy unit" is about the mover.
  by: PlayerId = target.controller,
): void {
  const from = target.location;
  if (!from || sameLocation(from, to)) return;
  target.movedFrom = from;
  target.location = to;
  target.movesThisTurn++;
  state.events.push({ t: 'move', object: target.id, from, to });
  // 144. Watched by cards at the location it left, not by the mover.
  state.pendingTriggers.push({
    on: 'unitMoves', player: target.controller, subject: target.id, from,
  });
  state.pendingTriggers.push({
    on: 'youMoveUnit', player: by, subject: target.id, from,
  });
  carryAttachments(state, target, to);
  touch(state);
}

/** 454. Relocation to base that is explicitly not a Move (456). */
export function recallToBase(state: GameState, target: GameObject): void {
  const from = target.location;
  const to = baseLocation(target.controller);
  if (!from || sameLocation(from, to)) return;
  target.location = to;
  carryAttachments(state, target, to);
  state.events.push({ t: 'recall', object: target.id, from, to });
  touch(state);
}

// -- resources ---------------------------------------------------------------

/** 429. Puts Energy or Power into a player's Rune Pool. */
export function addResources(
  state: GameState, player: PlayerId,
  energy: number, power: number, domain: Domain | 'any' | null,
  only: SpendOnly | null = null,
): void {
  const pool = state.players[player].pool;
  if (only && energy > 0) pool.restricted.push({ energy, only });
  else pool.energy += energy;
  if (power > 0) {
    if (domain === null || domain === 'any') pool.any += power;
    else pool.power[domain] += power;
  }
  if (energy > 0 || power > 0) {
    state.events.push({
      t: 'addResources', player, energy, power,
      domain: domain && domain !== 'any' ? domain : null,
    });
    touch(state);
  }
}

/** 167. Unspent Energy and Power are lost. */
export function emptyPools(state: GameState): void {
  for (const p of state.players) {
    p.pool = emptyPool();
    state.events.push({ t: 'poolEmptied', player: p.id });
  }
  touch(state);
}

// -- points and scoring ------------------------------------------------------

/** 194.4. Points never go below zero. */
export function gainPoints(
  state: GameState, _rules: Rules, player: PlayerId, amount: number,
): void {
  if (amount === 0) return;
  const p = state.players[player];
  p.points = Math.max(0, p.points + amount);
  state.events.push({ t: 'pointsGained', player, amount, total: p.points });
  touch(state);
}

/**
 * 467. Scoring a battlefield, by Hold or Conquer.
 *
 * 471.1.b is the rule that shapes the whole endgame: a Conquer that would take
 * you to within reach of victory only pays out if you have scored *every*
 * battlefield this turn. Otherwise you draw a card instead. Without it a player
 * could grind out the last point from a single safe battlefield.
 */
export function score(
  state: GameState, rules: Rules, player: PlayerId, index: number,
  method: 'hold' | 'conquer',
): boolean {
  const bf = battlefield(state, index);
  if (bf.scoredThisTurn.includes(player)) return false; // 470
  bf.scoredThisTurn.push(player);

  let points = 1;
  // The *effective* target, not the printed one: with Aspirant's Climb on the
  // board this restriction used to bite two points before it should.
  if (method === 'conquer'
    && state.players[player].points >= victoryTarget(state, rules) - 1) {
    const scoredAll = state.battlefields.every((b) => b.scoredThisTurn.includes(player));
    if (!scoredAll) {
      state.events.push({ t: 'finalPointDenied', player, battlefield: index });
      draw(state, rules, player, 1);
      points = 0;
    }
  }

  if (points > 0) gainPoints(state, rules, player, points);
  state.events.push({ t: 'score', player, battlefield: index, method, points });
  return points > 0;
}

// -- modifications -----------------------------------------------------------

export function addMightMod(
  state: GameState, target: GameObject, amount: number,
  duration: 'turn' | 'combat' | 'permanent', source: ObjectId,
): void {
  target.mods.push({ kind: 'might', amount, duration, source });
  touch(state);
}

export function grantKeyword(
  state: GameState, target: GameObject, keyword: Keyword, amount: number,
  duration: 'turn' | 'combat' | 'permanent', source: ObjectId,
): void {
  target.mods.push({ kind: 'keyword', keyword, amount, duration, source });
  touch(state);
}

/** 317.2.c / 466.7.c. Drops modifications whose window has closed. */
/**
 * 187.4. Hands back every borrowed permanent whose loan has ended.
 *
 * Two durations: until end of turn, and until the card that took it leaves the
 * board. The second is why this is checked on every settle rather than only in
 * the Expiration Step -- the taker can die at any moment.
 */
export function returnBorrowed(state: GameState, endOfTurn: boolean): void {
  for (const o of boardObjects(state)) {
    const loan = o.borrowedControl;
    if (!loan) continue;
    const taker = state.objects[loan.by];
    const over = loan.until === 'turn'
      ? endOfTurn
      : !taker || taker.zone.kind !== 'board';
    if (!over) continue;
    o.controller = loan.from;
    o.borrowedControl = null;
    touch(state);
  }
}

export function expireMods(state: GameState, duration: 'turn' | 'combat'): void {
  if (duration === 'turn') {
    // 706. Every object, not only the ones on the board: a spell that dealt
    // damage this turn is sitting in a trash, and "each time this spell has
    // dealt damage this turn" has to stop counting when the turn ends.
    for (const o of Object.values(state.objects)) {
      if (o?.damageInstancesThisTurn) delete o.damageInstancesThisTurn;
    }
  }
  for (const o of boardObjects(state)) {
    o.mods = o.mods.filter((m) => m.duration !== duration);
    if (duration === 'turn') {
      o.statuses = o.statuses.filter((s) => s !== 'stunned');
      o.movesThisTurn = 0;
      o.conqueredThisTurn = false;
      o.damagedThisTurn = false;
      o.usedThisTurn = [];
    }
  }
  touch(state);
}

/** 143.3.b. Heals every unit on the board. */
export function healAll(state: GameState, rules: Rules): void {
  for (const o of boardObjects(state)) {
    if (isUnitType(rules.index.get(o.card).type)) heal(state, o);
  }
}

// -- tokens ------------------------------------------------------------------

export function createToken(
  state: GameState, rules: Rules, cardId: string, controller: PlayerId,
  location: Location, ready = false,
): GameObject {
  const def = rules.index.get(cardId);
  const id = state.nextObjectId++;
  const token: GameObject = {
    id, card: cardId, owner: controller, controller,
    zone: { kind: 'board' }, location,
    // 143.4: units enter exhausted unless something says otherwise.
    exhausted: isUnitType(def.type) ? !ready : false,
    damage: 0, statuses: [], mods: [],
    attachedTo: null, attachments: [],
    enteredTurn: state.turn, movesThisTurn: 0, conqueredThisTurn: false,
    usedThisTurn: [], playedFrom: null,
    borrowedControl: null,
  };
  state.objects[id] = token;
  state.events.push({ t: 'play', player: controller, object: id, card: cardId, to: location });
  touch(state);
  return token;
}

/** Total resources across every domain, used by the AI's evaluation. */
export const domainList = (): readonly Domain[] => DOMAINS;
